/**
 * Pure helpers for the API-backed translation system (`utils/useTranslation`,
 * `utils/getTranslation`). Kept free of React so they can be unit-tested.
 */

export type TNestedDictionary = {[key: string]: string | TNestedDictionary}

/**
 * Flatten a nested translation object into colon-separated keys, mirroring the
 * API seed format (`initDictionaries.ts`). Example: `{common: {save: 'Save'}}`
 * becomes `{'common:save': 'Save'}`.
 */
export function flattenDictionary(
  obj: TNestedDictionary,
  prefix = '',
): Record<string, string> {
  const result: Record<string, string> = {}
  for (const key of Object.keys(obj)) {
    const value = obj[key]
    const fullKey = prefix ? `${prefix}:${key}` : key
    if (typeof value === 'object' && value !== null) {
      Object.assign(result, flattenDictionary(value, fullKey))
    } else {
      result[fullKey] = value
    }
  }
  return result
}

/**
 * Resolve a translation key, preferring the API-managed dictionary and falling
 * back to the bundled local dictionary, then to the raw key. Positional
 * interpolation replaces each `{{...}}` placeholder with the next param.
 */
export function resolveTranslation(
  key: string,
  primary: Record<string, string>,
  fallback: Record<string, string>,
  params?: (string | null)[] | null,
): string {
  let str = primary[key] ?? fallback[key] ?? key
  if (params && Array.isArray(params)) {
    let i = 0
    str = str.replace(/\{\{[^}]+}}/g, () => params[i++] ?? '')
  }
  return str
}
